import { useState } from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import duration, { DurationUnitsObjectType } from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { listStudentCourseEnrollmentsOptions } from '@drivingdoc/api-client/generated/react-query';

import FeedbackDialog from '../../FeedbackDialog';

dayjs.extend(duration);
dayjs.extend(localizedFormat);

const renderTimeCompleted = (time_completed: DurationUnitsObjectType, time_required: DurationUnitsObjectType) => {
  const completed_duration = dayjs.duration(time_completed ?? { minutes: 0 });
  const required_duration = dayjs.duration(time_required ?? { minutes: 0 });
  const text = `${completed_duration.asHours()} / ${required_duration.asHours()} Hours`;
  let color = undefined;

  if (completed_duration.asMilliseconds() === 0) {
    color = 'rgb(255, 59, 48)';
  } else if (completed_duration.asMilliseconds() < required_duration.asMilliseconds()) {
    color = 'rgb(255, 149, 0)';
  } else if (completed_duration.asMilliseconds() >= required_duration.asMilliseconds()) {
    color = 'rgb(52, 199, 89)';
  }
  return <span style={{ color }}>{text}</span>;
};

const columns: GridColDef[] = [
  {
    field: 'course_section',
    headerName: 'Section Title',
    type: 'string',
    sortable: false,
    minWidth: 300,
    valueGetter: (_value, row) => {
      return row?.course_section?.title ?? '';
    },
  },
  {
    field: 'enrolled_at',
    headerName: 'Enrolled',
    type: 'dateTime',
    sortable: false,
    width: 150,
    valueGetter: (value) => (!value ? null : new Date(value)),
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format('ll');
      }
      return null;
    },
  },
  {
    field: 'seat_secured_at',
    headerName: 'Seat Secured',
    type: 'dateTime',
    sortable: false,
    width: 150,
    valueGetter: (value) => (!value ? null : new Date(value)),
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format('ll');
      }
      return 'No';
    },
  },
  {
    field: 'performance_time_completed',
    headerName: 'Performance Progress',
    type: 'string',
    sortable: false,
    minWidth: 250,
    renderCell: (params) => {
      const time_completed = params.row.performance_time_completed;
      const time_required = params.row.course_completion_criteria.performance_time_required;

      if (time_required === 0) {
        return 'Not Required';
      }

      return renderTimeCompleted(time_completed, time_required);
    },
  },
  {
    field: 'observation_time_completed',
    headerName: 'Observation Progress',
    type: 'string',
    sortable: false,
    minWidth: 250,
    renderCell: (params) => {
      const time_completed = params.row.performance_time_completed;
      const time_required = params.row.course_completion_criteria.performance_time_required;

      if (time_required === 0) {
        return 'Not Required';
      }

      return renderTimeCompleted(time_completed, time_required);
    },
  },
  {
    field: 'completed_at',
    headerName: 'Completed',
    type: 'dateTime',
    sortable: false,
    width: 150,
    valueGetter: (value) => (!value ? null : new Date(value)),
    valueFormatter: (value) => {
      if (value) {
        return dayjs(value).format('ll');
      }
      return null;
    },
  },
];

interface SelectCourseEnrollmentDialogPayload {
  /**
   * A title for the dialog.
   */
  title?: React.ReactNode;

  // /**
  //  * The message to show in the dialog.
  //  */
  // message?: React.ReactNode

  /**
   * The text to show in the "Cancel" button. Defaults to `'Cancel'`.
   */
  cancelText?: React.ReactNode;

  /**
   * The text to show in the "Ok" button. Defaults to `'Ok'`.
   */
  okText?: React.ReactNode;

  /**
   * The student id to select an enrollment for.
   */
  studentId: string;

  /**
   * The number of enrollments that need to be selected. Defaults to 1.
   */
  selectionsRequired?: number;
}

export function SelectCourseEnrollmentDialog({
  payload,
  open,
  onClose,
}: DialogProps<SelectCourseEnrollmentDialogPayload, string[] | null>) {
  const {
    title = 'Select Course Enrollments',
    cancelText,
    okText = 'Ok',
    studentId,
    selectionsRequired = 1,
  } = payload || {};

  // Dialog State
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });

  const studentCourseEnrollments = useQuery({
    ...listStudentCourseEnrollmentsOptions({
      path: {
        id: studentId ?? '',
      },
    }),
    enabled: !!studentId,
  });

  const dialogs = useDialogs();

  const handleDoneSelecting = () => {
    // if a selection limit is set, verify that the user has selected enough enrollments
    if (rowSelectionModel.ids.size < selectionsRequired) {
      // show error message
      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: `You Must Select ${selectionsRequired} Enrollment${selectionsRequired > 1 ? 's' : ''}`,
      });

      return;
    }

    // close the dialog and return the selected enrollments
    onClose([...rowSelectionModel.ids] as string[]);
  };

  return (
    <Dialog maxWidth="xl" fullWidth open={open} onClose={() => onClose(null)}>
      <DialogTitle>
        {(title ?? selectionsRequired > 1) ? 'Select Course Enrollments' : 'Select A Course Enrollment'}
      </DialogTitle>

      <DialogContent>
        <DataGrid
          disableColumnFilter
          loading={studentCourseEnrollments.isLoading}
          columns={columns}
          rows={studentCourseEnrollments.data ?? []}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          checkboxSelection={selectionsRequired > 1}
          sx={{
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiInputBase-root': {
              width: 'auto!important',
            },
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button disabled={!open} onClick={() => onClose(null)}>
          {cancelText ?? 'Cancel'}
        </Button>

        <Button disabled={rowSelectionModel.ids.size < selectionsRequired} onClick={handleDoneSelecting}>
          {okText ?? 'Ok'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SelectCourseEnrollmentDialog;
